import React, { useState, useRef } from 'react'
import { ShieldAlert, Eye, EyeOff, Search, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react'
import { guestPlayerService } from '@/services/guestPlayerService'

interface PasswordCheckActivityProps {
  activityData: Record<string, any>
  disabled: boolean
  runCode: string
  onSubmitResponse: (data: Record<string, any>) => Promise<{ success: boolean; error?: string }>
  onComplete?: () => void
}

export function PasswordCheckActivity({ activityData, disabled, runCode, onSubmitResponse, onComplete }: PasswordCheckActivityProps) {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [checking, setChecking] = useState(false)
  const [timesSeen, setTimesSeen] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const submittedRef = useRef(false)

  const sha1Hash = async (text: string): Promise<string> => {
    const encoder = new TextEncoder()
    const data = encoder.encode(text)
    const hashBuffer = await crypto.subtle.digest('SHA-1', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase()
  }

  const handleCheck = async () => {
    if (!password || checking) return
    setChecking(true)
    setError(null)
    setTimesSeen(null)

    try {
      const hash = await sha1Hash(password)
      const prefix = hash.substring(0, 5)
      const suffix = hash.substring(5)

      const result = await guestPlayerService.hibpPasswordRange(runCode, prefix)
      if (result.success && result.data?.suffixes) {
        const lines = result.data.suffixes.split('\n')
        let count = 0
        for (const line of lines) {
          const [lineSuffix, lineCount] = line.split(':')
          if (lineSuffix?.trim() === suffix) {
            count = parseInt(lineCount?.trim() || '0', 10)
            break
          }
        }
        setTimesSeen(count)
        void recordParticipation(count)
      } else {
        setError(result.error || 'Failed to check password')
      }
    } catch {
      setError('Failed to check password')
    } finally {
      setChecking(false)
    }
  }

  // Record participation once, on the player's first check. Kept separate from
  // the "Check" action so players can keep experimenting with other passwords
  // without pressing a button to "finish".
  const recordParticipation = async (timesSeenCount: number) => {
    if (submittedRef.current) return
    submittedRef.current = true
    const result = await onSubmitResponse({
      password_checked: true,
      times_seen: timesSeenCount,
    })
    if (result.success) setSubmitted(true)
    else submittedRef.current = false
  }

  const formatCount = (n: number): string => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
    return n.toString()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold text-[var(--color-text)]">
        <ShieldAlert className="w-5 h-5 text-amber-500" />
        Password Check
      </div>

      {activityData?.instruction && (
        <p className="text-sm text-[var(--color-text-muted)]">{activityData.instruction}</p>
      )}

      <p className="text-sm text-[var(--color-text-muted)]">
        Check if a password has appeared in known data breaches. Your password is hashed in your browser — it never leaves your device.
      </p>

      {/* Password input — stays available so players can check multiple passwords */}
      {!disabled && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a password to check..."
                className="w-full px-3 py-2 pr-10 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm text-[var(--color-text)] bg-transparent"
                onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Uses k-anonymity: only the first 5 characters of the SHA-1 hash are sent. The password never leaves your browser.
            </p>
          </div>
          <button
            onClick={handleCheck}
            disabled={!password || checking}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {checking ? 'Checking...' : 'Check Password'}
          </button>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Results */}
      {timesSeen !== null && (
        <div className="space-y-3">
          {timesSeen === 0 ? (
            <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
              <div>
                <p className="font-medium text-green-400">Good news!</p>
                <p className="text-sm text-[var(--color-text-muted)]">This password was not found in any known data breaches.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0" />
              <div>
                <p className="font-medium text-rose-400">Password compromised!</p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  This password has been seen <strong className="text-rose-400">{formatCount(timesSeen)} times</strong> in data breaches. You should never use this password.
                </p>
              </div>
            </div>
          )}

          {submitted && onComplete && (
            <button
              onClick={onComplete}
              className="w-full py-2.5 rounded-lg font-semibold text-sm text-white"
              style={{ background: 'var(--gradient-primary)' }}
            >
              Next Activity
            </button>
          )}
        </div>
      )}

      {disabled && timesSeen === null && (
        <div className="p-4 text-center text-[var(--color-text-muted)]">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="font-medium">Already completed</p>
        </div>
      )}
    </div>
  )
}
